import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { selectChatIntentTypes } from '../../../accessors/index'
import { GET_RESOLVED_SALES_CHATS_STARTED } from '../../../actions/actionTypes'
import { updateResolvedChats } from '../../../actions/messaging'
import MultiSelect from '../../../components/ReusableComponents/DropdownDialogs/Multi/index'
import SingleSelect from '../../../components/ReusableComponents/DropdownDialogs/Single/index'
import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import SmartDatepicker from '../../../components/ReusableComponents/SmartDatepicker/index'
import ConversationRow from '../ConversationRow/index'
import { importantCampaignNames } from '../data'
import useTrainingFilters from '../useTrainingFilters'
import styles from './Sidebar.module.scss'

const Tab = ({ isActive, children, handleClick, count }) => (
  <div onClick={handleClick} className={isActive ? styles.activeTab : styles.tab}>
    {children} {isActive ? <>({count})</> : ''}
  </div>
)

const ChatBotTrainingSidebar = ({ data, categorizedChatIds, currentChatId }) => {
  const [filterState, filterDispatch] = useTrainingFilters()

  const { campaigns, category, startDate, endDate, take, skip } = filterState

  const prevCategoryRef = useRef(category)

  const setFilterStartDate = v => filterDispatch({ type: 'updateStartDate', payload: v })
  const setFilterEndDate = v => filterDispatch({ type: 'updateEndDate', payload: v })
  const setFilteredCategory = v => filterDispatch({ type: 'updateCategory', payload: v })

  const setFilteredCampaigns = v =>
    filterDispatch({ type: 'updateCampaigns', payload: v })

  const getMoreResults = () => filterDispatch({ type: 'getMoreResults' })

  const intentTypes = useSelector(selectChatIntentTypes)

  const isLoading = useSelector(s => s.chatBotTraining.resolvedThreadsLoading)

  const dispatch = useDispatch()

  const [showCategoryFilter, setShowCategoryFilter] = useState()
  const [showCampaignFilter, setShowCampaignFilter] = useState()

  const updateChats = useCallback(
    () =>
      dispatch(
        updateResolvedChats({
          take,
          skip,
          from: startDate.utc().format('YYYY/MM/DD'),
          to: endDate.utc().format('YYYY/MM/DD'),
          contentCategories: Object.keys(campaigns).filter(k => campaigns[k]),
          chatCatetoryId: category
        })
      ),
    [campaigns, category, dispatch, endDate, skip, startDate, take]
  )

  useEffect(() => {
    if (
      (prevCategoryRef.current === null && category !== null) ||
      (prevCategoryRef.current !== null && category === null)
    ) {
      updateChats()
    }

    prevCategoryRef.current = category
  }, [category, updateChats])

  const handleShowAlreadyCategorized = () => {
    dispatch({ type: GET_RESOLVED_SALES_CHATS_STARTED })
    filterDispatch({ type: 'showAlreadyCategorized' })
  }

  const handleShowNotCategorized = () => {
    dispatch({ type: GET_RESOLVED_SALES_CHATS_STARTED })
    filterDispatch({ type: 'showNotCategorized' })
  }

  if (isLoading) {
    return (
      <div className={styles.sidebar}>
        <LoadingIndicator />
      </div>
    )
  }

  return (
    <div className={styles.sidebar}>
      <div className={styles.tabs}>
        <Tab
          isActive={category === null}
          handleClick={handleShowNotCategorized}
          count={data.length}
        >
          Waiting review
        </Tab>
        <Tab
          isActive={category !== null}
          handleClick={handleShowAlreadyCategorized}
          count={data.length}
        >
          Already categorized
        </Tab>
      </div>
      <div className={styles.filterBar}>
        <div className={styles.filterHolder}>
          <SmartDatepicker
            isRange
            startDate={startDate}
            endDate={endDate}
            onChangeStartDate={setFilterStartDate}
            onChangeEndDate={setFilterEndDate}
            inputClassName={styles.filterValue}
          />
        </div>
        {category !== null && (
          <div className={styles.filterHolder}>
            <div
              onClick={() => setShowCategoryFilter(true)}
              className={styles.filterValue}
            >
              {intentTypes[category] && intentTypes[category].name}
            </div>
            {showCategoryFilter && (
              <SingleSelect
                options={Object.keys(intentTypes).reduce(
                  (prev, curr) => ({
                    ...prev,
                    [intentTypes[curr].id]: intentTypes[curr].name
                  }),
                  {}
                )}
                handleClose={() => setShowCategoryFilter(false)}
                selectedOption={category}
                handleChange={setFilteredCategory}
              />
            )}
          </div>
        )}
        <div className={styles.filterHolder}>
          <div onClick={() => setShowCampaignFilter(true)} className={styles.filterValue}>
            {Object.values(campaigns).filter(c => c).length <
            Object.values(campaigns).length
              ? Object.values(campaigns).filter(c => c).length + ' campaigns'
              : 'Any campaign'}
          </div>
          {showCampaignFilter && (
            <MultiSelect
              options={campaigns}
              labels={importantCampaignNames}
              handleClose={() => setShowCampaignFilter(false)}
              handleChange={e =>
                setFilteredCampaigns({
                  ...campaigns,
                  [e.target.value]: e.target.checked
                })
              }
            />
          )}
        </div>
        <button className={styles.goButton} onClick={updateChats}>
          Save
        </button>
      </div>
      <div className={styles.cards}>
        {data.map(c => (
          <ConversationRow
            active={Number(currentChatId) === c.chatId}
            key={c.chatId}
            subscriber={c.subscriber || 'Unknown guest name'}
            hotelName={c.subsidiaryName || 'Unknown hotel'}
            baseUrl="aiTraining"
            chatId={c.chatId}
            lastMessage={c.lastMessage}
            category={
              categorizedChatIds[c.chatId]
                ? intentTypes[categorizedChatIds[c.chatId]].name
                : null
            }
          />
        ))}
      </div>
      {data.length >= take && (
        <button className={styles.getMoreButton} onClick={getMoreResults}>
          Get more results
        </button>
      )}
      {data.length === 0 && (
        <div className={styles.noResults}>No chats found for selected dates.</div>
      )}
    </div>
  )
}
ChatBotTrainingSidebar.propTypes = {
  children: PropTypes.arrayOf(PropTypes.node)
}

export default ChatBotTrainingSidebar
